import { create } from 'zustand';

const PENDING_ORDER_STATUSES = new Set([1, '1', 'pending_admin_approval']);

function isPendingOrderStatus(status) {
  return PENDING_ORDER_STATUSES.has(status);
}

function getOrderPlacedAt(order) {
  return order?.orderDate || order?.placedAt || new Date().toISOString();
}

function getOrdersArray(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.orders)) return response.orders;
  return [];
}

function calculateCountdown(placedAt, rejectionSeconds) {
  const now = new Date();
  const placedAtDate = new Date(placedAt);
  const elapsedSeconds = Math.floor((now - placedAtDate) / 1000);
  return Math.max(0, rejectionSeconds - elapsedSeconds);
}

/**
 * Pending Orders Store
 * Manages pending orders with countdown timers
 */
const usePendingOrdersStore = create((set, get) => ({
  orders: [], // Array of { orderId, receiptNumber, placedAt, totalPrice, weight, countdown }
  rejectionSeconds: 120, // Default rejection time in seconds
  isMinimized: false, // Whether notifications are minimized

  /**
   * Set rejection seconds from settings
   */
  setRejectionSeconds: (seconds) => {
    set({ rejectionSeconds: seconds || 120 });
  },

  /**
   * Toggle minimized state
   */
  setMinimized: (minimized) => {
    set({ isMinimized: minimized });
  },

  /**
   * Add a pending order
   */
  addPendingOrder: (order) => {
    if (order?.id === undefined || order?.id === null || order?.id === '') {
      return;
    }

    const { orders, rejectionSeconds } = get();
    const placedAt = getOrderPlacedAt(order);
    const countdown = calculateCountdown(placedAt, rejectionSeconds);

    // Check if order already exists
    if (orders.find((o) => o.orderId === order.id)) {
      return;
    }

    set({
      orders: [
        ...orders,
        {
          orderId: order.id,
          receiptNumber: order.receiptNumber,
          placedAt,
          totalPrice: order.totalPrice,
          weight: order.weight || order.quantity,
          countdown,
        },
      ],
    });
  },

  /**
   * Remove a pending order
   */
  removePendingOrder: (orderId) => {
    set((state) => ({
      orders: state.orders.filter(
        (o) => String(o.orderId) !== String(orderId)
      ),
    }));
  },

  /**
   * Clear all pending orders (e.g. after hub reports approve/reject)
   */
  clearAllPendingOrders: () => {
    set({ orders: [], isMinimized: false });
  },

  /**
   * Update countdown for all orders
   * Returns array of expired order IDs
   */
  updateCountdown: () => {
    const { orders, rejectionSeconds } = get();

    const updatedOrders = orders.map((order) => {
      const countdown = calculateCountdown(order.placedAt, rejectionSeconds);
      return { ...order, countdown };
    });

    // Find expired orders before removing them
    const expiredOrderIds = updatedOrders
      .filter((order) => order.countdown <= 0)
      .map((order) => order.orderId);

    // Remove orders with countdown <= 0
    const activeOrders = updatedOrders.filter((order) => order.countdown > 0);

    set({ orders: activeOrders });
    
    return expiredOrderIds;
  },

  /**
   * Initialize pending orders from API
   */
  initializePendingOrders: async (ordersApi, rejectionSecondsOverride) => {
    try {
      const response = await ordersApi();
      const allOrders = getOrdersArray(response);
      const pendingOrders = allOrders.filter((order) => isPendingOrderStatus(order?.status));

      const { rejectionSeconds } = get();
      const effectiveRejectionSeconds = rejectionSecondsOverride ?? rejectionSeconds;

      const orders = pendingOrders
        .map((order) => {
          const placedAt = getOrderPlacedAt(order);
          const countdown = calculateCountdown(placedAt, effectiveRejectionSeconds);
          return {
          orderId: order.id,
          receiptNumber: order.receiptNumber || order.documentId || order.systemNumber,
          placedAt,
          totalPrice: order.totalPrice,
          weight: order.weight || order.quantity || order.count,
          countdown,
          };
        })
        .filter((order) => order.countdown > 0);

      set({ orders });
    } catch (error) {
      console.error('Error initializing pending orders:', error);
    }
  },

  /**
   * Recalculate all orders countdown when rejectionSeconds changes
   */
  recalculateCountdowns: () => {
    const { orders, rejectionSeconds } = get();

    const updatedOrders = orders.map((order) => {
      const countdown = calculateCountdown(order.placedAt, rejectionSeconds);
      return { ...order, countdown };
    });

    set({ orders: updatedOrders.filter((order) => order.countdown > 0) });
  },
}));

export default usePendingOrdersStore;

